import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import BlogEditor from "./BlogEditor";
import BlogSeoFields from "./BlogSeoFields";
import "../styles/admin-edit-modal.css";
import { image_url } from "../../confi";

const slugify = (text) =>
  text
    ?.toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '') || '';

const EditBlogModal = ({ blog, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: "",
    author_name: "",
    content: "",
    image: null,
    title_tag: "",
    meta_description: "",
    keywords: "",
    url_title: "",
    canonical_url: "",
    focus_keyword: "",
    og_title: "",
    og_description: "",
    meta_robots: "index, follow",
  });
  const [imagePreview, setImagePreview] = useState("");

  useEffect(() => {
    if (blog) {
      setFormData({
        title: blog.title || "",
        author_name: blog.author_name || "",
        content: blog.content || "",
        image: null,
        title_tag: blog.title_tag || blog.meta_title || "",
        meta_description: blog.meta_description || "",
        keywords: blog.keywords || blog.meta_keywords || "",
        url_title: blog.url_title || blog.slug || "",
        canonical_url: blog.canonical_url || "",
        focus_keyword: blog.focus_keyword || "",
        og_title: blog.og_title || "",
        og_description: blog.og_description || "",
        meta_robots: blog.meta_robots || "index, follow",
      });
      setImagePreview(
        blog.image?.startsWith("http")
          ? blog.image
          : `${image_url}/${blog.image}` || ""
      );
    }
  }, [blog]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      const file = files[0];
      if (file && file.type.startsWith("image/")) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error("Image size must be less than 5MB!");
          return;
        }
        setFormData((prev) => ({ ...prev, image: file }));
        setImagePreview(URL.createObjectURL(file));
      } else {
        toast.error("Please select a valid image file (JPG, PNG, WEBP).");
      }
    } else {
      setFormData((prev) => {
        const updated = { ...prev, [name]: value };
        if (name === "title" && (!prev.url_title || prev.url_title === slugify(prev.title))) {
          updated.url_title = slugify(value);
        }
        return updated;
      });
    }
  };

  const handleSlugGenerate = () => {
    if (formData.title) {
      setFormData((prev) => ({
        ...prev,
        url_title: slugify(prev.title),
      }));
      toast.info("URL slug generated from title!");
    } else {
      toast.warn("Please enter a blog title first.");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title || !formData.author_name || !formData.content) {
      toast.error("Please fill in all required fields (Title, Author/Category, Content).");
      console.log("data ", formData);
      return;
    }
    const finalSlug = formData.url_title || slugify(formData.title);
    const submissionData = {
      ...formData,
      url_title: finalSlug,
      title_tag: formData.title_tag || formData.title,
    };
    onSave(blog.id, submissionData);
  };

  return (
    <div className="admin-modal-overlay">
      <div className="admin-edit-modal">
        <div className="admin-edit-header">
          <h2 className="admin-edit-title">Edit Blog</h2>
          <button onClick={onClose} className="admin-close-button">×</button>
        </div>

        <form onSubmit={handleSubmit} className="admin-edit-form">
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label className="admin-form-label">Blog Title *</label>
              <input
                type="text"
                name="title"
                className="admin-form-input"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Category / Author *</label>
              <select
                name="author_name"
                className="admin-form-input"
                value={formData.author_name}
                onChange={handleChange}
                required
              >
                <option value="">Select Category</option>
                <option value="Housekeeping">Housekeeping</option>
                <option value="Baby Sitting">Baby Sitting</option>
                <option value="Elderly Care">Elderly Care</option>
                <option value="Patient Care">Patient Care</option>
                <option value="Cook">Cook</option>
                <option value="Driver">Driver</option>
              </select>
            </div>
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Upload Image</label>
            <input
              type="file"
              name="image"
              accept="image/*"
              className="admin-form-file"
              onChange={handleChange}
            />
          </div>

          {imagePreview && (
            <div className="admin-image-preview">
              <img src={imagePreview} alt="Preview" className="admin-preview-image" />
            </div>
          )}

          <div className="admin-form-group">
            <label className="admin-form-label">Blog Content *</label>
            <BlogEditor
              value={formData.content}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, content: value }))
              }
            />
          </div>

          {/* SEO & Meta Settings */}
          <BlogSeoFields
            formData={formData}
            imagePreview={imagePreview}
            onChange={handleChange}
            onSlugGenerate={handleSlugGenerate}
            mode="modal"
          />

          <div className="admin-form-actions">
            <button type="button" onClick={onClose} className="admin-button-secondary">
              Cancel
            </button>
            <button type="submit" className="admin-button-primary">
              Update Blog
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBlogModal;